'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { planService } from '../../../services/planService';

interface FormData {
  plan_name: string;
  dimensions: string;
  location: string;
  climate: string;
  sunlight: string;
  budget: string;
  objective: string;
  details: string;
}

export default function InputFormInteractive() {
  const router = useRouter();
  const [formData, setFormData] = useState<FormData>({
    plan_name: '',
    dimensions: '',
    location: '',
    climate: '',
    sunlight: '',
    budget: '',
    objective: '',
    details: ''
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFieldChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.plan_name?.trim()) newErrors.plan_name = 'Plan name is required';
    if (!formData.dimensions?.trim()) newErrors.dimensions = 'Dimensions are required';
    if (!formData.location?.trim()) newErrors.location = 'Location is required';
    if (!formData.climate?.trim()) newErrors.climate = 'Climate is required';
    if (!formData.sunlight?.trim()) newErrors.sunlight = 'Sunlight level is required';
    if (!formData.budget?.trim()) newErrors.budget = 'Budget is required';
    if (!formData.objective?.trim()) newErrors.objective = 'Objective is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGeneratePlan = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Do NOT save to database here to avoid duplicate inserts.
      // We only navigate to the AI plan page and let the user explicitly save.
      const queryParams = new URLSearchParams({
        plan_name: formData.plan_name,
        dimensions: formData.dimensions,
        location: formData.location,
        climate: formData.climate,
        sunlight: formData.sunlight,
        budget: formData.budget,
        objective: formData.objective,
        ...(formData.details && { details: formData.details })
      });

      router.push(`/ai-plan?${queryParams.toString()}`);
    } catch (error) {
      console.error('Error generating plan:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Space Assessment Form
            </h1>
            <p className="text-lg text-gray-600">
              Tell us about your space to get personalized optimization recommendations
            </p>
          </div>

          <form className="space-y-6">
            {/* Plan Name Field */}
            <div>
              <label htmlFor="plan_name" className="block text-sm font-medium text-gray-700 mb-2">
                Plan Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="plan_name"
                value={formData.plan_name}
                onChange={(e) => handleFieldChange('plan_name', e.target.value)}
                placeholder="e.g., Balcony Herb Garden, Rooftop Vegetable Plot"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${
                  errors.plan_name ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.plan_name && (
                <p className="mt-1 text-sm text-red-600">{errors.plan_name}</p>
              )}
            </div>

            {/* Dimensions Field */}
            <div>
              <label htmlFor="dimensions" className="block text-sm font-medium text-gray-700 mb-2">
                Dimensions <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="dimensions"
                value={formData.dimensions}
                onChange={(e) => handleFieldChange('dimensions', e.target.value)}
                placeholder="e.g., 12' x 8' or 3.6m x 2.4m"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${
                  errors.dimensions ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.dimensions && (
                <p className="mt-1 text-sm text-red-600">{errors.dimensions}</p>
              )}
            </div>

            {/* Location Field */}
            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                Location <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="location"
                value={formData.location}
                onChange={(e) => handleFieldChange('location', e.target.value)}
                placeholder="e.g., Balcony, Rooftop, Backyard, Indoor space"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${
                  errors.location ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.location && (
                <p className="mt-1 text-sm text-red-600">{errors.location}</p>
              )}
            </div>

            {/* Climate Field */}
            <div>
              <label htmlFor="climate" className="block text-sm font-medium text-gray-700 mb-2">
                Climate <span className="text-red-500">*</span>
              </label>
              <select
                id="climate"
                value={formData.climate}
                onChange={(e) => handleFieldChange('climate', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${
                  errors.climate ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select climate</option>
                <option value="tropical">Tropical</option>
                <option value="subtropical">Subtropical</option>
                <option value="mediterranean">Mediterranean</option>
                <option value="temperate">Temperate</option>
                <option value="continental">Continental</option>
                <option value="arid">Arid</option>
                <option value="polar">Polar</option>
                <option value="indoor">Indoor / Controlled Environment</option>
              </select>
              {errors.climate && (
                <p className="mt-1 text-sm text-red-600">{errors.climate}</p>
              )}
            </div>

            {/* Sunlight Level Dropdown */}
            <div>
              <label htmlFor="sunlight" className="block text-sm font-medium text-gray-700 mb-2">
                Sunlight Level <span className="text-red-500">*</span>
              </label>
              <select
                id="sunlight"
                value={formData.sunlight}
                onChange={(e) => handleFieldChange('sunlight', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${
                  errors.sunlight ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select sunlight level</option>
                <option value="low">Low (Less than 4 hours direct sun)</option>
                <option value="medium">Medium (4-6 hours direct sun)</option>
                <option value="high">High (More than 6 hours direct sun)</option>
              </select>
              {errors.sunlight && (
                <p className="mt-1 text-sm text-red-600">{errors.sunlight}</p>
              )}
            </div>

            {/* Budget Field */}
            <div>
              <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-2">
                Budget <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="budget"
                value={formData.budget}
                onChange={(e) => handleFieldChange('budget', e.target.value)}
                placeholder="Enter your budget (e.g., 500)"
                min="0"
                step="50"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${
                  errors.budget ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.budget && (
                <p className="mt-1 text-sm text-red-600">{errors.budget}</p>
              )}
            </div>

            {/* Objective Dropdown */}
            <div>
              <label htmlFor="objective" className="block text-sm font-medium text-gray-700 mb-2">
                Objective <span className="text-red-500">*</span>
              </label>
              <select
                id="objective"
                value={formData.objective}
                onChange={(e) => handleFieldChange('objective', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${
                  errors.objective ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select your primary objective</option>
                <option value="food">Food (Growing vegetables, herbs, fruits)</option>
                <option value="income">Income (Commercial growing, selling produce)</option>
                <option value="mixed">Mixed (Both food production and income)</option>
              </select>
              {errors.objective && (
                <p className="mt-1 text-sm text-red-600">{errors.objective}</p>
              )}
            </div>

            {/* Details Textarea */}
            <div>
              <label htmlFor="details" className="block text-sm font-medium text-gray-700 mb-2">
                Additional Details
              </label>
              <textarea
                id="details"
                value={formData.details}
                onChange={(e) => handleFieldChange('details', e.target.value)}
                placeholder="Any additional information about your space, preferences, or constraints..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-vertical"
              />
            </div>

            {/* Generate Plan Button */}
            <div className="pt-4">
              <button
                type="button"
                onClick={handleGeneratePlan}
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating Plan...
                  </span>
                ) : (
                  'Generate Plan'
                )}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Your space details will be analyzed by our AI to create a personalized optimization plan.</p>
          </div>
        </div>
      </div>
    </div>
  );
}